import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { shortDate } from "@/lib/format";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Package, ListChecks, Users, MessageSquare, Sparkles, Shield, Loader2, HeartPulse } from "lucide-react";

const TILES = {
  deliverables: { titleKey: "ctl.deliverables", icon: Package },
  todos: { titleKey: "ctl.todos", icon: ListChecks },
  clients: { titleKey: "ctl.clients", icon: Users },
  chats: { titleKey: "ctl.chats", icon: MessageSquare },
  weva: { titleKey: "ctl.weva", icon: Sparkles },
  members: { titleKey: "ctl.members", icon: HeartPulse },
};
const PERIODS = ["7d", "30d", "90d"];

function memberHealth(c) {
  const ts = c.last_activity_at;
  if (!ts) return { key: "silent", labelKey: "cir.health_still", cls: "bg-rose-100 text-rose-700" };
  const days = (Date.now() - new Date(ts).getTime()) / 86400000;
  if (days <= 7) return { key: "active", labelKey: "cir.health_active", cls: "bg-emerald-100 text-emerald-700" };
  if (days <= 14) return { key: "quiet", labelKey: "cir.health_quiet", cls: "bg-amber-100 text-amber-700" };
  return { key: "silent", labelKey: "cir.health_still", cls: "bg-rose-100 text-rose-700" };
}

export default function CircleTile() {
  const t = useT();
  const { user } = useAuth();
  const { type, tile } = useParams();
  const [searchParams] = useSearchParams();
  const circleId = searchParams.get("circleId") || "";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({});       // deliverables: kind -> count
  const [period, setPeriod] = useState("30d");   // weva
  const [weva, setWeva] = useState({ overview: null, latest: null, analyses: [] });

  const qp = useCallback(() => {
    const p = new URLSearchParams({ circle_type: type });
    if (type === "local" && circleId) p.set("circle_id", circleId);
    return p.toString();
  }, [type, circleId]);

  const loadTile = useCallback(async () => {
    setLoading(true);
    try {
      if (tile === "deliverables") {
        const { data: d } = await api.get(`/coach/circles/deliverables?${qp()}`);
        setData(d);
        const map = {};
        (d.deliverables || []).forEach((x) => { map[x.kind] = x.count_per_month; });
        setEdits(map);
      } else if (tile === "weva") {
        const [ov, lt, an] = await Promise.all([
          api.get(`/coach/circles/weva/group-overview?${qp()}&period=${period}`).then((r) => r.data).catch(() => null),
          api.get(`/coach/circles/weva/analysis/latest?${qp()}`).then((r) => r.data?.analysis).catch(() => null),
          api.get(`/coach/circles/weva/analyses?${qp()}`).then((r) => r.data?.analyses || []).catch(() => []),
        ]);
        setWeva({ overview: ov, latest: lt, analyses: an });
      } else if (tile === "members") {
        const { data: d } = await api.get(`/coach/circles/clients?${qp()}`);
        setData(d);
      } else {
        const { data: d } = await api.get(`/coach/circles/${tile}?${qp()}`);
        setData(d);
      }
    } catch (err) { toast.error(apiError(err, t("ctl.load_error"))); }
    finally { setLoading(false); }
  }, [tile, qp, period]);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    loadTile();
  }, [user, loadTile]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;
  const meta = TILES[tile];
  if (!meta) return <Navigate to={`/app/coach/circles/${type === "local" ? "local" : "inner"}`} replace />;

  const saveDeliverables = async () => {
    setBusy(true);
    try {
      const deliverables = Object.entries(edits)
        .map(([kind, count_per_month]) => ({ kind, count_per_month: Number(count_per_month) || 0 }))
        .filter((x) => x.count_per_month > 0);
      await api.put("/coach/circles/deliverables", { circle_type: type, circle_id: circleId || null, deliverables });
      toast.success(t("ctl.deliverables_saved"));
      await loadTile();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const runWeva = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/coach/circles/weva/analysis", { circle_type: type, circle_id: circleId || null, period });
      if (r.suppressed) toast.message(t("ctl.weva_too_few"));
      else toast.success(t("ctl.analysis_created"));
      await loadTile();
    } catch (err) { toast.error(apiError(err, t("ctl.analysis_pro"))); } finally { setBusy(false); }
  };

  const backTo = `/app/coach/circles/${type === "local" ? "local" : "inner"}${circleId ? `?circleId=${circleId}` : ""}`;

  return (
    <div data-testid="circle-tile-screen">
      <Seo title={t("ctl.seo", { title: t(meta.titleKey) })} noindex />
      <Link to={backTo} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="tile-back">
        <ChevronLeft className="h-4 w-4" /> {t("ctl.back")}
      </Link>
      <PageHeader eyebrow={type === "local" ? "Local Circle" : "Inner Circle"} title={t(meta.titleKey)} />

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : (
        <>
          {/* DELIVERABLES */}
          {tile === "deliverables" && (
            <div data-testid="circle-deliverables-screen">
              {type === "inner" ? (
                <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{t("ctl.inner_readonly")}</p>
              ) : null}
              <div className="space-y-2">
                {(data?.suggested_perks || []).map((p) => (
                  <div key={p.perk_id || p.kind} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{p.label || p.kind}</p>
                      {p.kind_hint && <p className="text-xs text-muted-foreground">{p.kind_hint}</p>}
                    </div>
                    <Input
                      type="number"
                      min="0"
                      className="w-20"
                      value={edits[p.kind] ?? ""}
                      onChange={(e) => setEdits((m) => ({ ...m, [p.kind]: e.target.value }))}
                      disabled={type === "inner"}
                      data-testid={`qty-${p.kind}`}
                    />
                  </div>
                ))}
                {(data?.suggested_perks || []).length === 0 && (
                  <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">{t("ctl.no_deliverables")}</p>
                )}
              </div>
              {type === "local" && (
                <Button className="mt-4 rounded-full" onClick={saveDeliverables} disabled={busy} data-testid="save-deliverables">
                  {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} {t("ctl.save")}
                </Button>
              )}
            </div>
          )}

          {/* TODOS */}
          {tile === "todos" && (
            <div data-testid="circle-todos-screen" className="space-y-5">
              <TodoBlock title={t("ctl.todo_behind")} items={data?.behind_deliverables} render={(x) => x.label || x.kind} emptyText={t("ctl.todo_behind_empty")} />
              <TodoBlock title={t("ctl.todo_dms")} items={data?.unanswered_dms} render={(x) => x.from_name || x.client_name || t("ctl.msg")} emptyText={t("ctl.todo_dms_empty")} />
              <TodoBlock title={t("ctl.todo_snapshots")} items={data?.recent_snapshots} render={(x) => x.client_name || x.title || t("ctl.snapshot")} emptyText={t("ctl.todo_snapshots_empty")} />
            </div>
          )}

          {/* CLIENTS */}
          {tile === "clients" && (
            <div data-testid="circle-clients-screen">
              {(data?.clients || []).length === 0 ? (
                <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("ctl.no_clients")}</p>
              ) : (
                <div className="space-y-2">
                  {data.clients.map((c) => (
                    <div key={c.id} data-testid={`client-card-${c.id}`} className="rounded-xl border border-border bg-card p-3">
                      <p className="font-semibold text-foreground">{c.name || c.email || t("ctl.client")}</p>
                      {c.email && <p className="text-xs text-muted-foreground">{c.email}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CHATS */}
          {tile === "chats" && (
            <div data-testid="circle-chats-screen">
              {data?.hard_limit_enabled && (
                <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{t("ctl.dm_limit", { n: data?.dm_limit_per_month })}</p>
              )}
              {(data?.threads || []).length === 0 ? (
                <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("ctl.no_chats")}</p>
              ) : (
                <div className="space-y-2">
                  {data.threads.map((th) => (
                    <div key={th.id || th.client_id} data-testid={`chat-thread-${th.id || th.client_id}`} className="rounded-xl border border-border bg-card p-3">
                      <p className="font-semibold text-foreground">{th.client_name || th.name || t("ctl.client")}</p>
                      {th.last_body && <p className="truncate text-sm text-muted-foreground">{th.last_body}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MEMBERS HEALTH */}
          {tile === "members" && (
            <div data-testid="circle-members-screen">
              {(data?.clients || []).length === 0 ? (
                <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("ctl.no_members")}</p>
              ) : (
                <>
                  <div className="mb-4 flex flex-wrap gap-2" data-testid="members-health-summary">
                    {[
                      { key: "active", labelKey: "cir.health_active", cls: "bg-emerald-100 text-emerald-700" },
                      { key: "quiet", labelKey: "cir.health_quiet", cls: "bg-amber-100 text-amber-700" },
                      { key: "silent", labelKey: "cir.health_still", cls: "bg-rose-100 text-rose-700" },
                    ].map((s) => {
                      const n = (data.clients || []).filter((c) => memberHealth(c).key === s.key).length;
                      return <span key={s.key} className={`rounded-full px-3 py-1 text-xs font-semibold ${s.cls}`}>{t(s.labelKey)}: {n}</span>;
                    })}
                  </div>
                  <div className="space-y-2">
                    {data.clients.map((c) => {
                      const h = memberHealth(c);
                      return (
                        <div key={c.client_id} data-testid={`member-health-${c.client_id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-semibold text-foreground">{c.name || t("ctl.client")}</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {t("ctl.entries_30d", { n: c.entries_30d ?? 0 })}{c.last_emotion ? t("ctl.last_emotion", { emotion: c.last_emotion }) : ""}
                            </p>
                          </div>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${h.cls}`}>{t(h.labelKey)}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* WEVA */}
          {tile === "weva" && (
            <div data-testid="circle-weva-screen">
              <div className="mb-4 flex items-center gap-2">
                {PERIODS.map((p) => (
                  <button key={p} type="button" onClick={() => setPeriod(p)} data-testid={`weva-period-${p}`}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${period === p ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                    {p}
                  </button>
                ))}
                <Button className="ml-auto gap-1.5 rounded-full" onClick={runWeva} disabled={busy} data-testid="run-weva-analysis">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("ctl.start_analysis")}
                </Button>
              </div>

              {weva.overview?.suppressed ? (
                <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("ctl.privacy_min")}</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-3">
                  <WevaList title={t("ctl.themes")} items={(weva.overview?.themes || []).map((x) => x.label || x.theme || x)} />
                  <WevaList title={t("ctl.emotions")} items={(weva.overview?.emotion_distribution || []).map((e) => `${e.emotion || e.label} ${e.count ? `(${e.count})` : ""}`)} />
                  <WevaList title={t("ctl.beliefs")} items={(weva.overview?.beliefs || []).map((b) => b.label || b.belief || b)} />
                </div>
              )}

              {weva.latest && (
                <div className="mt-6">
                  <p className="mb-2 text-sm font-semibold text-foreground">{t("ctl.latest_analysis")}</p>
                  <div className="space-y-2">
                    {(weva.latest.clusters || []).map((c) => (
                      <div key={c.id} className="rounded-xl border border-border bg-card p-3">
                        <p className="font-semibold text-foreground">{c.label}</p>
                        {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(weva.analyses || []).length > 0 && (
                <div className="mt-6">
                  <p className="mb-2 text-sm font-semibold text-foreground">{t("ctl.history")}</p>
                  <div className="space-y-1">
                    {weva.analyses.map((a) => (
                      <div key={a.id} data-testid={`weva-history-${a.id}`} className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2 text-sm">
                        <span className="text-foreground">{a.period || t("ctl.analysis")}</span>
                        <span className="text-muted-foreground">{shortDate(a.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="mt-6 flex items-start gap-2 rounded-xl bg-secondary/60 p-3 text-xs text-muted-foreground" data-testid="weva-privacy">
                <Shield className="mt-0.5 h-4 w-4 shrink-0" />
                {t("ctl.privacy_note")}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TodoBlock({ title, items, render, emptyText }) {
  const list = items || [];
  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-foreground">{title}</p>
      {list.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="space-y-1.5">
          {list.map((x, i) => (
            <div key={x.id || i} className="rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground">{render(x)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function WevaList({ title, items }) {
  const t = useT();
  const list = (items || []).filter(Boolean);
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="mb-2 text-sm font-semibold text-foreground">{title}</p>
      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("ctl.no_data")}</p>
      ) : (
        <ul className="space-y-1">
          {list.slice(0, 6).map((x, i) => <li key={i} className="text-sm text-muted-foreground">{x}</li>)}
        </ul>
      )}
    </div>
  );
}
